import { useState } from 'react';
import { BadgeCheck, Bug, CheckCircle2, Shirt, X } from 'lucide-react';
import { FileUpload, type UploadFile } from '@ampersandbrands/ui';
import {
  usePaperworkStatus,
  useSubmitPaperwork,
  type PaperworkOption,
  type PaperworkSubmission,
} from '@/lib/usePaperwork';

/**
 * Store paperwork (ISD-575): photograph a CINTAS invoice, a Pest Control
 * invoice, or a license and send it to Store Ops. The photos are saved to the
 * store's Store Info files in Cockpit, under Paperwork/<category>. CINTAS is
 * expected twice a month and Pest Control once; the top of the page shows how
 * this month stands, and Sweet Spot reminders go out on the 1st, 15th and 21st.
 */

// The buttons come from the API (status.options). Icons stay here, keyed by code.
const ICONS: Record<string, typeof Shirt> = { cintas: Shirt, pest_control: Bug };

function optionIcon(code: string): typeof Shirt {
  return ICONS[code] ?? BadgeCheck;
}

// "CINTAS invoice", "Pest Control invoice", "License".
function optionTitle(o: PaperworkOption): string {
  return o.types.length > 0 ? o.label.replace(/s$/, '') : `${o.label} invoice`;
}

const MAX_PHOTOS = 10;

// Both kinds of entry are needed. 'image/*' is what makes the store tablet app
// (Capacitor) open the camera when Add photos is tapped; the exact types are
// what the shared FileUpload checks each file against, since it compares the
// file's type literally and would reject a photo on 'image/*' alone.
const PHOTO_TYPES = ['image/*', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp', 'application/pdf'];

const labelClass =
  'mb-1 block text-[12px] font-semibold uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted';
const inputClass =
  'w-full rounded-lg border border-candy-line bg-candy-cream px-3 py-2 text-sm text-candy-ink placeholder:text-candy-ink-muted focus:outline-none focus:ring-2 focus:ring-candy-pink dark:border-candy-dark-line dark:bg-candy-dark-bg dark:text-candy-dark-ink dark:placeholder:text-candy-dark-ink-muted dark:focus:ring-candy-pink-dark';
const cardClass =
  'rounded-xl border border-candy-line bg-candy-white p-4 shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface';

function monthName(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, 1).toLocaleString('en-US', { month: 'long' });
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}

function ThisMonth() {
  const statusQ = usePaperworkStatus();
  const status = statusQ.data;
  if (!status) return null;
  return (
    <div className={`${cardClass} mb-5`}>
      <p className="mb-2 text-[13px] font-semibold text-candy-ink dark:text-candy-dark-ink">
        {monthName(status.period)} so far
      </p>
      <ul className="space-y-1.5">
        {status.categories.map((c) => {
          const done = c.have >= c.need;
          return (
            <li key={c.code} className="flex items-center justify-between text-sm">
              <span className="text-candy-ink dark:text-candy-dark-ink">{c.label}</span>
              <span
                className={`inline-flex items-center gap-1 font-medium ${
                  done ? 'text-candy-ink-muted dark:text-candy-dark-ink-muted' : 'text-candy-pink dark:text-candy-pink-dark'
                }`}
              >
                {done && <CheckCircle2 className="h-4 w-4" aria-hidden />}
                {Math.min(c.have, c.need)} of {c.need} sent
              </span>
            </li>
          );
        })}
      </ul>
      {status.recent.length > 0 && (
        <p className="mt-3 text-[12px] text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Last sent: {status.recent[0]!.label}
          {status.recent[0]!.doc_date ? `, ${shortDate(status.recent[0]!.doc_date)}` : ''} (
          {status.recent[0]!.files} photo{status.recent[0]!.files === 1 ? '' : 's'})
        </p>
      )}
    </div>
  );
}

export function Paperwork() {
  const statusQ = usePaperworkStatus();
  const options = statusQ.data?.options ?? [];
  const [option, setOption] = useState<PaperworkOption | null>(null);
  const [typeCode, setTypeCode] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [photos, setPhotos] = useState<UploadFile[]>([]);
  const [done, setDone] = useState(false);
  const submitMut = useSubmitPaperwork();

  const hasTypes = !!option && option.types.length > 0;
  const code = hasTypes ? typeCode : (option?.code ?? '');
  const needsNote = code === 'license_other';
  const canSubmit = !!option && photos.length > 0 && !!code && (!needsNote || note.trim().length > 0);

  function openForm(o: PaperworkOption) {
    setOption(o);
    setTypeCode('');
    setDate('');
    setNote('');
    setPhotos([]);
    setDone(false);
    submitMut.reset();
  }

  function closeForm() {
    setOption(null);
    setDone(false);
    submitMut.reset();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!option || !canSubmit) return;
    const body: PaperworkSubmission = {
      category: code,
      ...(date ? { doc_date: date } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    await submitMut.mutateAsync({ body, files: photos.map((p) => p.file) });
    setDone(true);
  }

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Paperwork</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Photograph invoices and licenses for Store Ops. Only HQ sees these.
        </p>
      </div>

      <ThisMonth />

      {!option && (
        <div className="grid gap-3">
          {options.map((o, i) => {
            const Icon = optionIcon(o.code);
            return (
              <button
                key={o.code}
                type="button"
                onClick={() => openForm(o)}
                className={
                  i === 0
                    ? 'inline-flex items-center justify-center gap-2 rounded-xl bg-candy-pink px-4 py-3 text-[14px] font-semibold text-white shadow-1 hover:opacity-90'
                    : 'inline-flex items-center justify-center gap-2 rounded-xl border border-candy-line bg-candy-white px-4 py-3 text-[14px] font-semibold text-candy-ink shadow-1 hover:bg-candy-cream-warm dark:border-candy-dark-line dark:bg-candy-dark-surface dark:text-candy-dark-ink dark:hover:bg-candy-dark-surface-warm'
                }
              >
                <Icon className="h-4 w-4" aria-hidden /> {optionTitle(o)}
              </button>
            );
          })}
        </div>
      )}

      {option && (
        <div className={cardClass}>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">
              Upload a {optionTitle(option)}
            </h2>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-full p-1 text-candy-ink-muted hover:bg-candy-cream-warm dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
              aria-label="Close form"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {done ? (
            <div className="py-2 text-center">
              <p className="text-[15px] font-semibold text-candy-ink dark:text-candy-dark-ink">Sent to Store Ops</p>
              <p className="mt-1 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">Thank you!</p>
              <div className="mt-4 flex justify-center gap-2">
                <button
                  type="button"
                  onClick={() => openForm(option)}
                  className="rounded-pill bg-candy-cream-warm px-4 py-2 text-sm font-medium text-candy-ink hover:bg-candy-line dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink"
                >
                  Upload another
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-pill bg-candy-cream-warm px-4 py-2 text-sm font-medium text-candy-ink hover:bg-candy-line dark:bg-candy-dark-surface-warm dark:text-candy-dark-ink"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              {hasTypes && (
                <div>
                  <label htmlFor="paperwork-type" className={labelClass}>
                    Type *
                  </label>
                  <select
                    id="paperwork-type"
                    value={typeCode}
                    onChange={(e) => setTypeCode(e.target.value)}
                    required
                    className={inputClass}
                  >
                    <option value="">Pick one…</option>
                    {option.types.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="paperwork-date" className={labelClass}>
                  {hasTypes ? 'Expires on' : 'Invoice date'}
                </label>
                <input
                  id="paperwork-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="paperwork-note" className={labelClass}>
                  {needsNote ? 'Note *' : 'Note'}
                </label>
                <textarea
                  id="paperwork-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  required={needsNote}
                  placeholder={needsNote ? 'What kind of license is this?' : 'Anything Store Ops should know'}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Photos *</label>
                {/* Several photos, because a multi-page invoice or license is
                    one record. Each photo gets its own upload link. */}
                <FileUpload
                  files={photos}
                  onChange={setPhotos}
                  multiple
                  maxFiles={MAX_PHOTOS}
                  accept={PHOTO_TYPES}
                  capture="environment"
                  label="Add photos"
                />
              </div>
              {submitMut.error && <p className="text-[12px] text-red-500">Could not send. Please try again.</p>}
              <button
                type="submit"
                disabled={!canSubmit || submitMut.isPending}
                className="w-full rounded-pill bg-candy-pink py-2.5 text-[14px] font-semibold text-white shadow-1 hover:opacity-90 disabled:opacity-50 dark:bg-candy-pink-dark"
              >
                {submitMut.isPending ? 'Sending…' : 'Send to Store Ops'}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
